import app from 'flarum/forum/app';
import { extend, override } from 'flarum/common/extend';
import DiscussionListItem from 'flarum/forum/components/DiscussionListItem';
import DiscussionListState from 'flarum/forum/states/DiscussionListState';
import IndexPage from 'flarum/forum/components/IndexPage';
import IndexSidebar from 'flarum/forum/components/IndexSidebar';
import extractText from 'flarum/common/utils/extractText';
import Link from 'flarum/common/components/Link';
import LinkButton from 'flarum/common/components/LinkButton';
import SelectDropdown from 'flarum/common/components/SelectDropdown';
import tagsLabel from 'ext:flarum/tags/common/helpers/tagsLabel';
import sortTags from 'ext:flarum/tags/common/utils/sortTags';
import ItemList from 'flarum/common/utils/ItemList';
/**
 * /all and the tag pages as a card feed rather than a discussion list.
 *
 * linkrobins.com is a publishing channel: in the 90 days before this was
 * written, Karl started 42 of 43 discussions and everyone else wrote 24 replies
 * (2026-10-26). A forum row says "thread"; a card says "post". Flarum stays
 * underneath — the same list, the same accounts, comments and support — only
 * how an entry is drawn changes.
 *
 * Four pieces, each scoped to feed routes only (Following, search results and
 * profile lists keep Flarum's own rows; they are lists you work through, not
 * lists you read):
 *   1. the list asks for each discussion's first post, to excerpt it;
 *   2. an item is a card;
 *   3. the sidebar's tag menu becomes a row of filter chips (and, on a phone,
 *      the phone bar's switcher: "All posts ▾", "Guides ▾"...);
 *   4. the toolbar goes (sort and mark-all-read are for working a list).
 *
 * The phone bar follows the site's one rule (Karl, 2026-10-27): the section
 * name in the middle as a switcher, one primary action on the right, only for
 * people allowed to use it. Core lifts .App-titleControl and
 * .App-primaryControl out of the sidebar into the bar, so this only has to
 * offer them.
 *
 * Ordering is the SERVER's: extend.php defaults the discussion index to newest
 * first, and the client sends no sort of its own, so the preloaded first page
 * and a client-side navigation cannot disagree.
 */
export default function blogIndex() {
  // The server sends 21 a page (extend.php, and TagPageContent for tag pages), but the
  // preloaded payload carries no meta.perPage, so the list fell back to core's 20 and
  // "Load More" asked for offset 20, repeating the last card. The list counts in 21s too.
  DiscussionListState.DEFAULT_PAGE_SIZE = 21;

  // The index endpoint declares firstPost (extend.php), but a request that names
  // its own `include` replaces the defaults, and core's list names its own.
  extend(DiscussionListState.prototype, 'requestParams', function (params) {
    if (!isFeed()) return;
    const include = Array.isArray(params.include) ? params.include : String(params.include || '').split(',').filter(Boolean);
    if (!include.includes('firstPost')) include.push('firstPost');
    params.include = include;
  });

  // The `Page` root class every feed rule in forum.less hangs off, added the
  // way flarum/tags adds IndexPage--tag<id>: to the vnode core just returned.
  extend(IndexPage.prototype, 'view', function (vdom) {
    try {
      if (isFeed() && vdom?.attrs) vdom.attrs.className = `${vdom.attrs.className || ''} IndexPage--feed`.trim();
    } catch (e) {
      /* a class name is never worth a blank page */
    }
  });

  // contentItems(), not items(): that is where core adds the toolbar.
  extend(IndexPage.prototype, 'contentItems', function (items) {
    if (isFeed()) items.remove('toolbar');
  });

  // The tag menu becomes chips. 'newDiscussion' stays where it is: on a phone
  // it IS the header bar's primary control (App-primaryControl), and the
  // stylesheet turns it into a small secondary action beside the chips on
  // larger screens.
  extend(IndexSidebar.prototype, 'items', function (items) {
    try {
      if (!isFeed()) return;
      items.remove('nav');
      items.add('feedChips', tagChips(), 50);
      // The phone bar's switcher. CSS shows it on phones only; the chips do
      // that job everywhere else, and the two never show together.
      items.add('feedNav', feedNav(), -10);
    } catch (e) {
      /* keep Flarum's own menu if anything above is missing */
    }
  });

  // Only people who may start a discussion get the button at all — core would
  // show everyone a disabled one, and a guest one that opens a login prompt.
  // The permission is the gate (it also blocks the composer and the API).
  extend(IndexSidebar.prototype, 'items', function (items) {
    try {
      const tag = app.currentTag?.();
      const can = app.forum.attribute('canStartDiscussion') && (!tag || tag.canStartDiscussion());
      if (!can) {
        items.remove('newDiscussion');
        return;
      }

      // "+", like every other section's primary control (support, wiki,
      // dashboard), rather than core's pencil.
      const button = items.get('newDiscussion');
      if (button?.attrs) button.attrs.icon = 'fas fa-plus';
    } catch (e) {
      /* leave core's button */
    }
  });
  override(DiscussionListItem.prototype, 'contentItems', function (original) {
    if (!isFeed()) return original();
    const items = new ItemList();
    items.add('card', m("article", {
      className: "LrCard"
    }, cardBody(this.attrs.discussion)));
    return items;
  });
}

/** Is this a list that should read as a feed? /all reports `index`, a tag page `tag`. */
export function isFeed() {
  const route = app.current?.get?.('routeName');
  return route === 'index' || route === 'tag';
}

/** The primary tags in the order the admin panel sets. Child tags are left out. */
function feedTags() {
  return sortTags(app.store.all('tags')).filter(t => t.position() !== null && !t.isChild());
}

/** Is this tag the page's tag, or the parent of the page's tag? */
function tagIsActive(tag, current) {
  return !!current && (current === tag || current.parent?.() === tag);
}

/**
 * The phone bar's switcher: names the view you are on ("All posts", "Guides")
 * and lists All posts plus each visible tag, navigating to /all or /t/<slug>.
 * Built like support's and the dashboard's: a SelectDropdown carrying
 * App-titleControl, which core lifts into the middle of the bar.
 */
function feedNav() {
  const params = app.search.state.stickyParams();
  const current = app.currentTag?.();
  const tags = feedTags();
  const allLabel = app.translator.trans('linkrobins-lr-home.forum.index.all_posts');
  const links = [m(LinkButton, {
    href: app.route('index', params),
    active: !current
  }, allLabel)];
  tags.forEach(tag => {
    links.push(m(LinkButton, {
      href: app.route('tag', {
        ...params,
        tags: tag.slug()
      }),
      active: tagIsActive(tag, current)
    }, tag.name()));
  });
  const label = current ? current.name() : allLabel;
  return m(SelectDropdown, {
    className: "App-titleControl LrFeedNav",
    buttonClassName: "Button",
    accessibleToggleLabel: app.translator.trans('linkrobins-lr-home.forum.index.switch_label', {
      current: extractText(label)
    }),
    defaultLabel: label
  }, links);
}

/**
 * All, then the primary tags in the order the admin panel sets. Child tags are
 * left out; the tag page itself lists its children.
 */
function tagChips() {
  const params = app.search.state.stickyParams();
  const current = app.currentTag?.();
  const tags = feedTags();
  return m("div", {
    className: "LrChips",
    role: "group",
    "aria-label": String(app.translator.trans('linkrobins-lr-home.forum.index.filter_label'))
  }, m(Link, {
    className: `LrChip${current ? '' : ' is-active'}`,
    href: app.route('index', params),
    "aria-current": current ? undefined : 'page'
  }, app.translator.trans('linkrobins-lr-home.forum.index.all')), tags.map(tag => {
    const active = tagIsActive(tag, current);
    return m(Link, {
      className: `LrChip${active ? ' is-active' : ''}`,
      href: app.route('tag', {
        ...params,
        tags: tag.slug()
      }),
      "aria-current": active ? 'page' : undefined
    }, tag.name());
  }));
}
function cardBody(discussion) {
  // A Flarum relationship that has not been loaded comes back as `false`, and
  // `?.` does not short-circuit false — so check the shape, not just presence.
  const first = discussion.firstPost?.();
  const html = first && typeof first.contentHtml === 'function' ? first.contentHtml() : null;
  const replies = discussion.replyCount?.() ?? 0;
  const created = discussion.createdAt?.() || null;
  const rawTags = discussion.tags?.();
  const tag = Array.isArray(rawTags) && rawTags.length ? sortTags(rawTags.filter(Boolean))[0] : null;
  return [m("div", {
    className: "LrCard-meta"
  }, tag ? m("span", {
    className: "LrCard-tag"
  }, tagsLabel([tag], {
    link: false
  })) : null, created ? m("time", {
    className: "LrCard-date",
    datetime: created.toISOString()
  }, shortDate(created)) : null), m("h2", {
    className: "LrCard-title"
  }, m(Link, {
    href: app.route.discussion(discussion)
  }, discussion.title())), html ? m("p", {
    className: "LrCard-excerpt"
  }, excerpt(html)) : null, m("div", {
    className: "LrCard-foot"
  }, m("span", {
    className: "LrCard-replies"
  }, app.translator.trans('linkrobins-lr-home.forum.index.replies', {
    count: replies
  })))];
}

/** "Oct 26, 2026" — a post carries a date, not "3 days ago". */
function shortDate(d) {
  try {
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return '';
  }
}

/**
 * The opening of the post, cut at a word.
 *
 * Read from the post's HTML rather than from core's plain-text form, because
 * core's conversion ends in `.replace(/\s+/g, ' ')` — every block boundary is
 * gone by then, and a note that opens with a sentence and then a heading came
 * out as "…read on a phone. Route a category to one person A support category…".
 * Here the blocks are still elements, so this can stop at the end of one.
 * (Core's own excerpt only exists for search results, which load
 * mostRelevantPost; a plain list has none.)
 *
 * Quotes, code and images contribute nothing to a two-line summary and go
 * first; a leading heading is usually the post's own title again, so it is
 * skipped when there is anything after it.
 */
function excerpt(html, limit) {
  if (limit === void 0) {
    limit = 190;
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('blockquote, script, style, pre, img, figure, table').forEach(el => el.remove());
  const blocks = Array.from(doc.body.children).map(el => ({
    heading: /^H[1-6]$/.test(el.tagName),
    text: (el.textContent || '').replace(/\s+/g, ' ').trim()
  })).filter(b => b.text !== '');
  while (blocks.length > 1 && blocks[0].heading) blocks.shift();

  // A post with no element children at all is bare text in the body.
  const source = blocks.length ? blocks : [{
    text: (doc.body.textContent || '').replace(/\s+/g, ' ').trim()
  }];
  let out = '';
  for (const block of source) {
    out = out ? `${out} ${block.text}` : block.text;
    if (out.length >= 90) break;
  }
  if (out.length <= limit) return out;
  const cut = out.slice(0, limit);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[,;:.\-—\s]+$/, '') + '…';
}